/* v8 ignore start - imports & type-only interface / SonarQube */ /* NOSONAR */
import React from 'react'

import { CollectionFiltersDS } from '@src/components/blocks/collection-filters-ds/collection-filters-ds'
import {
  FilterSectionWithDrawer,
  FilterSectionWithDrawerProps,
  MobileFilters,
  UpdateQueryParams,
} from '@src/quince-components/src/atoms/filterSection/menu'
import type { SortOption } from '@src/quince-components/src/atoms/filterSection/menu/filterSection.interface'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { COLOR_PAGES_RENDERED_PATHS } from '../../collection.constants'
import Collection, { COLLECTION_PAGE_TYPE, SubCollection } from '../../domains/Collection'
import { Filter, SelectedFilter } from '../../domains/CollectionFilter'
import { IProductFeedResponseForCollection } from '../../services/collectionService/CollectionService.types'

interface CollectionFiltersProps {
  totalProductsCount: number
  initialFilters: Filter[]
  sortBy: string
  selectedFilters: SelectedFilter
  updateQuery: (params: UpdateQueryParams) => void
  collectionSlug?: string
  collectionPage?: Collection | IProductFeedResponseForCollection
  showLoader?: boolean
  subCollectionCards: SubCollection[]
  filtersContainerClassName?: string
  sortOptions?: SortOption[]
}
/* v8 ignore stop */ /* NOSONAR */

function buildBaseProps({
  totalProductsCount,
  initialFilters,
  sortBy,
  selectedFilters,
  updateQuery,
  collectionSlug,
  collectionPage,
  showLoader,
  subCollectionCards,
  filtersContainerClassName,
  sortOptions,
}: CollectionFiltersProps): FilterSectionWithDrawerProps {
  const isBundleCollection =
    collectionPage && 'collectionPageType' in collectionPage
      ? collectionPage.collectionPageType === COLLECTION_PAGE_TYPE.BUNDLE
      : false

  return {
    noOfItems: totalProductsCount,
    filters: initialFilters,
    sortBy,
    selectedFilters,
    updateQuery: updateQuery as (params: UpdateQueryParams) => void,
    showSelectedOption: sortBy !== '',
    shouldRenderColorFilterHtml:
      collectionPage && COLOR_PAGES_RENDERED_PATHS.has('slug' in collectionPage ? collectionPage.slug : ''),
    collectionSlug: collectionSlug ?? (collectionPage && 'slug' in collectionPage ? collectionPage.slug : ''),
    hideFilters: isBundleCollection,
    skipSorting: isBundleCollection,
    sortOptions,
    showShimmer: showLoader,
    subCollectionCards,
    filtersContainerClassName,
  }
}

const CollectionFiltersLegacy = (props: CollectionFiltersProps) => {
  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP
  const Component = isDesktop ? FilterSectionWithDrawer : MobileFilters
  return <Component {...buildBaseProps(props)} />
}

const CollectionFilters = (props: CollectionFiltersProps) => {
  if (shouldUseDesignSystem()) {
    // No SectionContentContainer here: the DS bar renders its own sticky full-bleed
    // band (it must escape the content frame to span the viewport when stuck).
    return <CollectionFiltersDS {...buildBaseProps(props)} />
  }
  return <CollectionFiltersLegacy {...props} />
}

export default CollectionFilters
